import React, { useContext, useEffect, useState, useRef } from "react"
import { ajvResolver } from "@hookform/resolvers/ajv"
import { Alert, CircularProgress, Card, Box, ModalClose, Tabs, TabList, Tab, TabPanel } from "@mui/joy"
import { Stepper, Step, StepLabel } from '@mui/material';
import { fullFormats } from "ajv-formats/dist/formats"
import { useForm, useWatch, FormProvider } from "react-hook-form"
import { Backdrop } from "@mui/material"
import { BreadCrumb, Button, CopyToClipboard, DetailTitle, Input, MirrorFields, Tag, Text } from "..";
import { useTranslation } from 'react-i18next';
import { useFormVisibilityHandler, useFieldReadonlyHandler } from "../../utils";
import { useFieldWatcher } from "./detail.hooks"
import { useSelector } from "react-redux";
import { AppContext } from "src/mtska-frontend-app-scaffolding"
import { RelationshipManager } from "../Relationship"
import JSONPretty from 'react-json-pretty';
import 'react-json-pretty/themes/monikai.css';

/** Paths (dot notation) under rootKey that are dirty in react-hook-form's dirtyFields subtree. */
const collectDirtyPathsUnderRoot = (rootKey, node) => {
    if (node === true) return [rootKey];
    if (!node || typeof node !== 'object') return [];
    const out = [];
    for (const [k, v] of Object.entries(node)) {
        const prefix = `${rootKey}.${k}`;
        if (v === true) out.push(prefix);
        else if (v && typeof v === 'object' && !Array.isArray(v)) {
            out.push(...collectDirtyPathsUnderRoot(prefix, v));
        } else if (Array.isArray(v)) {
            v.forEach((item, i) => {
                if (item === true) out.push(`${prefix}.${i}`);
                else if (item && typeof item === 'object') {
                    out.push(...collectDirtyPathsUnderRoot(`${prefix}.${i}`, item));
                }
            });
        }
    }
    return out;
};

/** `incoming` wins on explicit keys; keeps branches only on `base`. Null on `incoming` does not wipe a rich object on `base` (partial RHF/API snapshots). */
const deepMergePreferIncoming = (base, incoming) => {
    if (incoming === undefined) return base;
    if (incoming === null) {
        return base != null && typeof base === 'object' && !Array.isArray(base) ? base : incoming;
    }
    if (typeof incoming !== 'object' || Array.isArray(incoming)) {
        return incoming;
    }
    if (base == null || typeof base !== 'object' || Array.isArray(base)) {
        return incoming;
    }
    const out = { ...base };
    for (const k of Object.keys(incoming)) {
        const b = base[k];
        const inc = incoming[k];
        if (inc !== null && typeof inc === 'object' && !Array.isArray(inc) && b != null && typeof b === 'object' && !Array.isArray(b)) {
            out[k] = deepMergePreferIncoming(b, inc);
        } else if (inc === null && b != null && typeof b === 'object' && !Array.isArray(b)) {
            out[k] = b;
        } else {
            out[k] = inc;
        }
    }
    return out;
};

const Detail = ({ dataProviderName, editingDefault = false, detailStack, detailConfig, modelName, readonly = false, values, onEditing = () => { }, save = () => { }, onProcessStepAction = () => true, onValidateStepAction = () => true, onChangeFieldValueAction = () => true, saving, loading: loadingProp, errors, errorMessage, alertMessage, isDrawer, isEmbedded, rightButtons, title, subtitle = "", header, canSave, hideSaveButton = false, icon = "", id, initialStepActive = 0, disableDetailConfigOverride = false }) => {

    const { t } = useTranslation();

    const recordId = values?._id ?? values?.id;
    const detailConfigOverride = useSelector((state) => {
        if (disableDetailConfigOverride) return undefined;
        if (!dataProviderName || !recordId) return undefined;
        return state?.[dataProviderName]?.global?.detailConfigOverrideById?.[recordId];
    });
    const effectiveDetailConfig = detailConfigOverride ?? detailConfig;

    const derivedLoading = useSelector((state) => {
        if (!dataProviderName || !id) return false;
        const comps = state?.[dataProviderName]?.components;
        if (!comps || typeof comps !== 'object') return false;

        return Object.values(comps).some((c) => c?.loading && c?.item === id);
    });
    const loading = (typeof loadingProp === 'boolean') ? loadingProp : derivedLoading;

    const editing = true;
    const [modifiedFields, setModifiedFields] = useState({});
    const [formValues, setFormValues] = useState({});

    useEffect(() => { onEditing(true); }, []);

    useEffect(() => {
        if (typeof values !== 'object') {
            return;
        }

        // sovrascrivo i valori ricevuti con lo stato dei valori modificati e non ancora salvati
        const merged = { ...values, ...modifiedFields };
        if (
            values?.finalData != null && typeof values.finalData === 'object' && !Array.isArray(values.finalData) &&
            modifiedFields?.finalData != null && typeof modifiedFields.finalData === 'object' && !Array.isArray(modifiedFields.finalData)
        ) {
            merged.finalData = deepMergePreferIncoming(values.finalData, modifiedFields.finalData);
        }
        const currentHash = JSON.stringify(formValues);
        const newHash = JSON.stringify(merged);

        // invoco solo se il nuovo set è diverso da quello già noto
        if (currentHash !== newHash) {
            setFormValues(merged);
            //console.info("received values", values, "modifiedFields", modifiedFields);
        } else {
            //console.info("merged is equal to previous state", merged, formValues);
        }

    }, [values])

    // FORM 
    const { handleSubmit, control, setError, setValue, formState, getValues, resetField, getFieldState, watch } = useForm({
        values: formValues,
        resolver: ajvResolver(effectiveDetailConfig?.validation ?? [], { formats: fullFormats, }),
    })

    const { dirtyFields } = formState;

    const watchedFields = useWatch({ control })
    useFieldWatcher({ setValue, watchedFields, editing })

    const isObject = (el) => {
        return (typeof el === 'object' && el !== null && !Array.isArray(el));
    }

    useEffect(() => {
        if (!editing) return;

        //console.info('+++una guardata ce la do')
        for (const [key, value] of Object.entries(watchedFields)) {
            const fieldState = getFieldState(key);

            //console.info('+++verifico field ', key, fieldState?.isDirty)
            /**
             * @todo: FIX
             * quando si edita un campo in finalData, il Detail concreto intercetta e sposta submit_action a "manual"
             * Tornando a "create", la key "submit_action" non è dirty ma la key "finalData" si. 
             * 
             * Questo genera il problema che non viene scatenata in Detail concreto la condizione per cui,
             * essendo cambiato submit_action, richiediamo il relativo dryrun.
             * 
             * La soluzione è gestire in onChangeFieldValueAction del Detail concreto 
             * un flag di stato per intercettare i cambi automatici e manuali su submit_actions
             **/
            if (fieldState?.isDirty) {
                setModifiedFields(prev => {
                    if (prev[key] === value) return prev; // evita update ridondanti per valori scalari
                    if (isObject(prev[key]) && isObject(value) && JSON.stringify(prev[key]) == JSON.stringify(value)) {
                        return prev; // evita update ridondanti per valori oggetto
                    }

                    //console.info('+++trovato field modificato', { [key]: value })

                    const dirtyPathsUnderKey = collectDirtyPathsUnderRoot(key, dirtyFields[key]);
                    onChangeFieldValueAction(key, value, setFormValues, stepActive, setValue, getValues, dirtyPathsUnderKey);
                    return { ...prev, [key]: value };
                });
            }
        }
    }, [watchedFields, editing, dirtyFields]);

    useEffect(() => {
        if (typeof errors !== 'object') return;
        Object.entries(errors).map(
            ([k, v], i) => setError(k, { type: 'server', message: v[0] })
        )
    }, [errors])

    const saveConfirmDialog = effectiveDetailConfig?.saveConfirmDialog;
    const [pendingSaveData, setPendingSaveData] = useState(null);

    const localHandleSubmit = (data) => {
        if (saveConfirmDialog?.active) {
            setPendingSaveData(data);
        } else {
            save(data);
        }
    };

    const executeSave = (extraProps = {}) => {
        const data = pendingSaveData;
        setPendingSaveData(null);
        save({ ...data, ...extraProps });
    };

    const { userCapabilities, user } = useContext(AppContext);
    const { verifyCondition } = useFormVisibilityHandler();
    const getDrawerClassName = () => {
        let cn = ['drawer-section-content'];

        cn.push(userCapabilities['auth/role.is_driver'] ? 'driver' : '');
        cn.push(userCapabilities['auth/role.is_carmanager'] ? 'carmanager' : '');
        cn.push(userCapabilities['auth/role.is_fleetmanager'] ? 'fleetmanager' : '');
        cn.push(userCapabilities['auth/role.is_superuser'] ? 'superuser' : '');

        cn.filter((cne) => cne !== '');

        return cn.join(' ');
    }

    // STEPPER
    const [stepActive, setStepActive] = useState(() => initialStepActive);
    const [stepTotal, setStepTotal] = useState(effectiveDetailConfig?.steps?.length ?? 0);
    const [stepBackDisabled, setStepBackDisabled] = useState(() => initialStepActive === 0);
    const [stepForwardDisabled, setStepForwardDisabled] = useState(false);
    const [stepForwardLabel, setStepForwardLabel] = useState(() => {
        const total = effectiveDetailConfig?.steps?.length ?? 0;
        if (total < 1) return 'Next';
        return initialStepActive === total - 1 ? 'Submit' : 'Next';
    });

    useEffect(() => {
        if (stepTotal < 1) return;
        setStepForwardLabel(stepActive === stepTotal - 1 ? 'Submit' : 'Next');
        setStepBackDisabled(stepActive === 0);
    }, [stepActive, stepTotal]);

    const onStepActionInvoked = async (actionInvoked) => {
        if (actionInvoked == 0) {
            return;
        }

        const processResult = onProcessStepAction(stepActive, stepTotal, actionInvoked, getValues, setValue);

        if (processResult instanceof Promise) {
            await processResult.then((resultValue) => {
                //console.info('ORA POSSO PROSEGUIRE', processResult, resultValue);
                const valid = onValidateStepAction(stepActive, getValues, setValue, setError, resultValue);

                if (resultValue.goToStep && valid) {
                    onStepActionExecute(actionInvoked, resultValue.goToStep - actionInvoked);
                } else if (valid) {
                    onStepActionExecute(actionInvoked, stepActive);
                }
            }).catch((error) => {
                console.error('Errore durante il salvataggio:', error);
            });
        } else if (processResult) {
            onStepActionExecute(actionInvoked, stepActive);
        }
    }

    const onStepActionExecute = (actionInvoked, currentStep) => {
        if (actionInvoked > 0 && currentStep < stepTotal - 1) {
            currentStep += 1;
            setStepActive(currentStep);
        } else if (actionInvoked > 0 && currentStep == stepTotal - 1) {
            //alert('final');
        } else if (actionInvoked < 0) {
            currentStep -= 1
            setStepActive(currentStep);
        }
    }

    useEffect(() => {
        setStepTotal(effectiveDetailConfig?.steps?.length ?? 0);
    }, [effectiveDetailConfig]);

    return (
        <>
            {isDrawer && (
                <>
                    <Box className="drawer-section-header" >
                        <ModalClose />
                        <DetailTitle title={title} subtitle={subtitle} icon={icon} />
                        <CopyToClipboard value={values?._id} />
                        {typeof effectiveDetailConfig?.steps === 'object' ? (
                            <Box display={"flex"} flexDirection="row"
                                justifyContent={"end"}
                                gap={1}
                                flexGrow={1}>
                                <Button
                                    label={'Back'}
                                    onClick={() => onStepActionInvoked(-1)}
                                    disabled={stepBackDisabled}
                                />
                                <Button
                                    label={stepForwardLabel}
                                    onClick={() => onStepActionInvoked(1)}
                                    disabled={stepForwardDisabled}
                                    loading={loading || saving}
                                />
                            </Box>
                        ) : (
                            <Box display={"flex"} flexDirection="row"
                                justifyContent={"end"}
                                gap={1}
                                flexGrow={1}>
                                {Array.isArray(rightButtons) && rightButtons.map((v, k) =>
                                    <div key={k}>{v}</div>
                                )}
                                {!hideSaveButton && (
                                    <Button
                                        label={t("Save")}
                                        onClick={canSave && handleSubmit(localHandleSubmit)}
                                        loading={loading || saving}
                                        disabled={!canSave}
                                    />
                                )}
                            </Box>
                        )}

                    </Box>

                    <BreadCrumb />
                </>
            )}
            {errorMessage && (
                <Alert color="danger">{errorMessage}</Alert>
            )}
            <Box className={getDrawerClassName() + (isEmbedded ? ' drawer-section-content-embedded form-element col-12' : '')}>

                <Backdrop open={saving || loading} style={{ backgroundColor: "#FFFFFF20" }}>
                    <CircularProgress />
                </Backdrop>

                {header}

                {!loading && (
                    <Card className="form-wrapper">
                        {/* <JSONPretty data={getValues()} className="json-wrapper" />  */}
                        {/* <JSONPretty data={formValues} className="json-wrapper" /> */}

                        <FormProvider handleSubmit={handleSubmit} control={control} setError={setError} setValue={setValue} formState={formState} getValues={getValues} resetField={resetField} getFieldState={getFieldState} watch={watch}>
                            <div className="form-section-fields-wrapper">

                                {alertMessage && (
                                    <Tag color="danger" size="medium" icon="faWarning">{alertMessage}</Tag>
                                )}

                                {typeof effectiveDetailConfig?.steps === 'object' && (
                                    <StepsSystem
                                        steps={effectiveDetailConfig.steps}
                                        stepActive={stepActive}
                                        stepTotal={stepTotal}
                                        values={values}
                                        formValues={formValues}
                                        setValue={setValue}
                                        watch={watch}
                                        control={control}
                                        modelName={modelName}
                                        dataProviderName={dataProviderName}
                                        detailStack={detailStack}
                                        handleSubmit={handleSubmit}
                                        localHandleSubmit={localHandleSubmit}
                                        editing={editing}
                                        readonly={readonly}
                                    />
                                )}

                                {typeof effectiveDetailConfig?.columns === 'object' && (
                                    <ColumnsSystem
                                        columns={effectiveDetailConfig.columns}
                                        heightMatch={effectiveDetailConfig.heightMatch ?? 'shortest'}
                                        values={values}
                                        formValues={formValues}
                                        setValue={setValue}
                                        watch={watch}
                                        control={control}
                                        modelName={modelName}
                                        dataProviderName={dataProviderName}
                                        detailStack={detailStack}
                                        handleSubmit={handleSubmit}
                                        localHandleSubmit={localHandleSubmit}
                                        editing={editing}
                                        readonly={readonly}
                                    />
                                )}

                                {typeof effectiveDetailConfig?.tabs === 'object' && (
                                    <TabsSystem
                                        tabs={effectiveDetailConfig.tabs}
                                        values={values}
                                        formValues={formValues}
                                        setValue={setValue}
                                        watch={watch}
                                        control={control}
                                        modelName={modelName}
                                        dataProviderName={dataProviderName}
                                        detailStack={detailStack}
                                        handleSubmit={handleSubmit}
                                        localHandleSubmit={localHandleSubmit}
                                        editing={editing}
                                        readonly={readonly}
                                    />
                                )}

                            </div>
                        </FormProvider>
                    </Card>
                )}

                {
                    // values?.data_ids && values?.data_ids.map((data_id) => <ImportData importdataid={data_id} />)
                }

                {/* <JSONPretty data={detailStack} className="json-wrapper" style={{ overflow: 'scroll', fontSize: '10px' }}></JSONPretty> */}
            </Box>

            {pendingSaveData !== null && (
                <Box className="dialog dialog-confirm">
                    <Box className="dialog-backdrop"></Box>
                    <Box className="dialog-content">
                        <Text level="h3">{t("Confirm save")}</Text>
                        <Text level="body-md">{t("How do you want to save?")}</Text>
                        <Box className="dialog-actions">
                            <Button variant="soft" className="dialog-ko"
                                onClick={() => setPendingSaveData(null)}>
                                {t("Cancel")}
                            </Button>
                            <Button variant="outlined"
                                onClick={() => executeSave()}>
                                {t("Save")}
                            </Button>
                            {(saveConfirmDialog?.additionalButtons ?? []).map((btn, i) => {
                                const enabled = !btn.visibilitycondition || verifyCondition(btn, watch, user);
                                return (
                                    <Button key={i} variant="solid"
                                        disabled={!enabled}
                                        onClick={() => executeSave({ [btn.name]: btn.value })}>
                                        {t(btn.label)}
                                    </Button>
                                );
                            })}
                        </Box>
                    </Box>
                </Box>
            )}
        </>

    )
}

const StepsSystem = ({ steps, stepActive, stepTotal, values, formValues, setValue, watch, control, modelName, dataProviderName, detailStack, handleSubmit, localHandleSubmit, editing, readonly }) => {

    const { t } = useTranslation();

    return <>
        <Stepper activeStep={stepActive} sx={{ marginBottom: '40px', marginTop: '10px' }} className="mtska-steps-stepper">
            {steps.map((step, index) => {
                const stepProps = {};
                const labelProps = {};
                //const stepError = validateStep(index);
                // if (stepError) {
                //     labelProps.optional = (
                //         <Typography variant="caption" color="error">
                //             {stepError}
                //         </Typography>
                //     );

                //     labelProps.error = true;
                // }
                return (
                    <Step key={step.name} {...stepProps}>
                        <StepLabel {...labelProps}>{t(step.name)}</StepLabel>
                    </Step>
                );
            })}
        </Stepper>

        <Box className="mtska-steps-container">
            {steps.map((step, index) => (
                <Box key={index} className={"mtska-step mtska-step-" + index + " " + (index === stepActive ? "mtska-step-visible" : "mtska-step-hidden")} style={index === stepActive ? { display: 'block' } : { display: 'none' }}>
                    {/* {index + 1} di {stepTotal} */}
                    {Array.isArray(step.tabs) && (
                        <TabsSystem
                            tabs={step.tabs}
                            values={values}
                            formValues={formValues}
                            setValue={setValue}
                            watch={watch}
                            control={control}
                            modelName={modelName}
                            dataProviderName={dataProviderName}
                            detailStack={detailStack}
                            handleSubmit={handleSubmit}
                            localHandleSubmit={localHandleSubmit}
                            editing={editing}
                            readonly={readonly}
                        />
                    )}
                    {Array.isArray(step.columns) && (
                        <ColumnsSystem
                            columns={step.columns}
                            heightMatch={step.heightMatch ?? 'shortest'}
                            values={values}
                            formValues={formValues}
                            setValue={setValue}
                            watch={watch}
                            control={control}
                            modelName={modelName}
                            dataProviderName={dataProviderName}
                            detailStack={detailStack}
                            handleSubmit={handleSubmit}
                            localHandleSubmit={localHandleSubmit}
                            editing={editing}
                            readonly={readonly}
                        />
                    )}
                </Box>
            ))}
        </Box>
    </>
}


const ColumnsSystem = ({ columns, heightMatch, values, formValues, setValue, watch, control, modelName, dataProviderName, detailStack, handleSubmit, localHandleSubmit, editing, readonly }) => {
    const containerRef = useRef(null);
    const { t } = useTranslation();

    useEffect(() => {
        if (heightMatch !== "shortest") return;

        const items = containerRef.current?.querySelectorAll(".mtska-column") ?? [];
        if (!items.length) return;

        const syncToShortest = () => {
            const minH = Math.min(
                ...[...items].map(i => i.querySelector(":scope > .mtska-tabs")?.scrollHeight || 0)
            );
            items.forEach(i => (i.style.height = `${(minH + 10)}px`));
        };

        syncToShortest();

        const ro = new ResizeObserver(syncToShortest);
        items.forEach(i => ro.observe(i.querySelector(":scope > .mtska-tabs")));

        return () => ro.disconnect();
    }, [heightMatch, columns]);

    return <Box ref={containerRef} className="mtska-columns-container" sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>

        {columns.map((column, index) => {
            return (
                <Box key={index} className={"mtska-column" + (column?.name ? ' ' + column?.name : ' mtska-column-noname')} sx={{ width: 'calc(' + column.width + ' - 5px)', maxWidth: column.width, overflowY: column.overflowY ?? 'scroll', maxHeight: column.maxHeight ?? '100%' }}>
                    {column?.name && (<h2 className="mtska-column-title">{t(column.name)}</h2>)}
                    <TabsSystem
                        tabs={column.tabs}
                        values={values}
                        formValues={formValues}
                        setValue={setValue}
                        watch={watch}
                        control={control}
                        modelName={modelName}
                        dataProviderName={dataProviderName}
                        detailStack={detailStack}
                        handleSubmit={handleSubmit}
                        localHandleSubmit={localHandleSubmit}
                        editing={editing}
                        readonly={readonly}
                    />
                </Box>
            )
        })}
    </Box>
}

const TabsSystem = ({ tabs, values, formValues, setValue, watch, control, modelName, dataProviderName, detailStack, handleSubmit, localHandleSubmit, editing, readonly }) => {

    const [tabIdSelected, setTabIdSelected] = useState(0)
    const { t } = useTranslation();

    const { user } = useContext(AppContext);

    const { verifyCondition } = useFormVisibilityHandler();
    const { verifyCondition: verifyReadonlyCondition } = useFieldReadonlyHandler();

    const getValueByPath = (obj, path) => {
        return path.split(/[\.\[\]]+/)
            .filter(Boolean)
            .reduce((acc, key) => acc?.[key], obj);
    };

    const normalizeToArray = (k) => {
        let val = getValueByPath(watch(), k);
        if (typeof val === "undefined") {
            val = getValueByPath(formValues, k);
        }
        if (val == null) {
            val = [];
        } else if (!Array.isArray(val)) {
            val = [val]
        }
        //console.info('normalizeToArray', k, val, '<---');
        return val;
    };

    useEffect(() => {
        const tabSelected = tabs[tabIdSelected];
        const isTabSelectedVisible = verifyCondition(tabSelected, watch, user);

        if (!tabSelected || !isTabSelectedVisible) {
            for (const [k, tab] of Object.entries(tabs)) {
                if (verifyCondition(tab, watch, user)) {
                    setTabIdSelected(parseInt(k));
                    break;
                }
            }
        }
    }, [values]);

    return <Tabs aria-label="Basic tabs" value={tabIdSelected} className="mtska-tabs" >
        {Object.entries(tabs).length > 1 && (
            <Box className="mtska-tab-list-scroller">
                <TabList className="mtska-tab-list">
                    {Object.entries(tabs).map(([k, tab]) => {
                        let visible = verifyCondition(tab, watch, user);
                        return (visible ?
                            <Tab
                                key={k}
                                className="mtska-tab"
                                data-type="tab"
                                data-name={tab.name}
                                onMouseUp={() => setTabIdSelected(parseInt(k))}
                            >
                                {t(tab.name)}
                            </Tab>
                            :
                            // Dobbiamo comunque disegnarlo perché altrimenti saltano i riferimenti tra tab e panel
                            <Tab key={k} className="mtska-tab mtska-tab-hidden" data-type="tab" data-name={tab.name}>
                                {t(tab.name)} NASCOSTO
                            </Tab>
                        )
                    })}
                </TabList>
            </Box>
        )}
        {Object.entries(tabs).map(([k, tab]) => {
            let visible = verifyCondition(tab, watch, user);

            return (visible ?
                <TabPanel className="mtska-tab-panel" value={parseInt(k)} key={k} data-type="tabpanel" data-name={tab.name}>
                    {
                        Object.entries(tab.fieldsets).map(([k, fieldset]) => {

                            let visible = verifyCondition(fieldset, watch, user);
                            //console.info('Visibility condition fieldset', fieldset.name, visible);
                            return (visible &&
                                <fieldset key={k} className="mtska-fieldset form-section-fields-content" data-type="fieldset" data-name={fieldset.name}>
                                    {Object.entries(tab.fieldsets).length > 1 && (
                                        <Text level="title-md" className={"mtska-fieldset-title form-element fullwidth"}>{t(fieldset.name)}</Text>
                                    )}
                                    {
                                        Object.entries(fieldset.fields).map(([k, field]) => {
                                            let visible = verifyCondition(field, watch, user);
                                            if (typeof field === 'object' && field !== null && field.readonlycondition) {
                                                field.readonly = verifyReadonlyCondition(field, watch);
                                            }

                                            if (field.type === 'relationship' || field.type === 'relationshipSingle') {
                                                return (visible &&
                                                    <RelationshipManager
                                                        dataProviderName={dataProviderName}
                                                        parentitem={values}
                                                        detailStack={detailStack}
                                                        modelName={modelName}
                                                        handleSubmit={handleSubmit(localHandleSubmit)}
                                                        editing={editing}
                                                        key={k}
                                                        name={k}
                                                        label={k}
                                                        value={normalizeToArray(k)}
                                                        setValue={setValue}
                                                        readonly={readonly}
                                                        multiple={field.type === 'relationship' ? true : false}
                                                        mirrorField={field.copiable?.active && <MirrorFields name={k} formValues={watch()} setValue={setValue} source={field.copiable?.source} destination={field.copiable?.destination} isRelationship={true} />}
                                                        {...field}
                                                    />
                                                );

                                            } else {
                                                return (visible &&
                                                    <Input
                                                        handleSubmit={handleSubmit(localHandleSubmit)}
                                                        editing={editing}
                                                        control={control}
                                                        key={k}
                                                        name={k}
                                                        label={k}
                                                        item={formValues}
                                                        detailStack={detailStack}
                                                        modelName={modelName}
                                                        setValue={setValue}
                                                        mirrorField={field.copiable?.active && <MirrorFields name={k} formValues={watch()} setValue={setValue} source={field.copiable?.source} destination={field.copiable?.destination} />}
                                                        {...field}
                                                    />
                                                )
                                            }
                                        })
                                    }
                                </fieldset>
                            )
                        })
                    }
                </TabPanel>
                :
                // Dobbiamo comunque disegnarlo perché altrimenti saltano i riferimenti tra tab e panel
                <TabPanel className="mtska-tab-panel" value={parseInt(k)} key={k} data-name={tab.name}>
                    {tab.name}
                </TabPanel>
            )
        })}
    </Tabs>
}

export default Detail;